import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Gamepad2Icon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const DEBOUNCE_MS = 400;
const LIMIT = 10;

const Games = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formStatus, setFormStatus] = useState('active');

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedSearch(searchTerm.trim()), DEBOUNCE_MS);
    return () => clearTimeout(tmr);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const loadGames = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(LIMIT));
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);

        const response = await api.get(`/games?${params.toString()}`);
        const data = response.data?.data || {};
        if (response.data?.success) {
          setGames(data.games || []);
          setTotal(data.total ?? 0);
          setTotalPages(data.totalPages ?? 1);
          setCurrentPage(data.page ?? page);
        } else {
          setGames([]);
        }
      } catch (error) {
        console.error('fetch games', error);
        toast.error(error.response?.data?.message || t('games.fetchError'), { position: 'top-center' });
        setGames([]);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, statusFilter, t]
  );

  useEffect(() => {
    loadGames(currentPage);
  }, [loadGames, currentPage]);

  const openCreate = () => {
    setEditingId(null);
    setFormName('');
    setFormStatus('active');
    setShowModal(true);
  };

  const openEdit = (game) => {
    setEditingId(game.id);
    setFormName(game.name || '');
    setFormStatus(game.status === 'inactive' ? 'inactive' : 'active');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formName.trim();
    if (!name) {
      toast.error(t('games.nameRequired'), { position: 'top-center' });
      return;
    }
    setIsSaving(true);
    try {
      const payload = { name, status: formStatus };
      if (editingId != null) {
        const response = await api.put(`/games/${editingId}`, payload);
        if (response.data?.success) {
          toast.success(response.data.message || t('games.updateSuccess'), { position: 'top-center' });
          closeModal();
          await loadGames(currentPage);
        }
      } else {
        const response = await api.post('/games', payload);
        if (response.data?.success) {
          toast.success(response.data.message || t('games.createSuccess'), { position: 'top-center' });
          closeModal();
          await loadGames(1);
          setCurrentPage(1);
        }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || (editingId != null ? t('games.updateError') : t('games.createError')),
        { position: 'top-center' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (game) => {
    if (!window.confirm(t('games.deleteConfirm', { name: game.name }))) return;
    try {
      const response = await api.delete(`/games/${game.id}`);
      if (response.data?.success) {
        toast.success(response.data.message || t('games.deleteSuccess'), { position: 'top-center' });
        await loadGames(currentPage);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('games.deleteError'), { position: 'top-center' });
    }
  };

  const formatUpdated = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Gamepad2Icon className="h-8 w-8 text-primary-600 shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('games.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('games.subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-primary-700"
        >
          <PlusIcon className="h-5 w-5" />
          {t('games.addGame')}
        </button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('games.searchPlaceholder')}
          className="w-full max-w-md rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
        />
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">{t('games.filterByStatus')}</span>
          <select
            id="games-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="all">{t('games.statusAll')}</option>
            <option value="active">{t('games.statusActive')}</option>
            <option value="inactive">{t('games.statusInactive')}</option>
          </select>
        </label>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
          </div>
        ) : games.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Gamepad2Icon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('games.noGames')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('games.noGamesHint')}</p>
            <button
              type="button"
              onClick={openCreate}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              <PlusIcon className="h-4 w-4" />
              {t('games.addFirst')}
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-gray-600">
                      {t('games.colName')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-gray-600">
                      {t('games.colStatus')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-gray-600">
                      {t('games.colUpdated')}
                    </th>
                    <th className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-gray-600">
                      {t('games.colId')}
                    </th>
                    <th className="px-4 py-3 text-end text-xs font-semibold uppercase tracking-wide text-gray-600">
                      {t('games.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {games.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{row.name}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                        {t(`games.statusLabel.${row.status}`, { defaultValue: row.status })}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{formatUpdated(row.updatedAt)}</td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-500">{row.id}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-end">
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="me-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-primary-600 hover:bg-primary-50"
                        >
                          <EditIcon className="h-4 w-4" />
                          <span>{t('games.edit')}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-red-600 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                          <span>{t('games.delete')}</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
                <p className="text-sm text-gray-600">
                  {t('games.showingPage', { page: currentPage, pages: totalPages, total })}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50"
                  >
                    <ChevronLeftIcon className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                  </button>
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50"
                  >
                    <ChevronRightIcon className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pb-20 pt-4 sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeModal} aria-hidden />
          <div className="relative z-10 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingId != null ? t('games.modalEditTitle') : t('games.modalAddTitle')}
            </h2>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('games.fieldName')} *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  autoFocus
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('games.fieldStatus')}</label>
                <select
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="active">{t('games.statusActive')}</option>
                  <option value="inactive">{t('games.statusInactive')}</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  {isSaving ? t('common.loading') : editingId != null ? t('games.save') : t('games.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Games;
